import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import { PersistConfig, persistReducer, persistStore } from "redux-persist";
import { getVersionNumber, isProd } from "@lib/helpers/General";
import storage from "redux-persist/lib/storage";

import auth from "./slices/auth";
import settings from "./slices/settings";
import notifications from "./slices/notifications";
import websocket from "./slices/websocket";
import messageBot from "./slices/messageBot";
import screen from "./slices/screen";
import cache from "./slices/cache";

const rootReducer = combineReducers({
  auth: auth,
  settings: settings,
  notifications: notifications,
  websocket: websocket,
  messageBot: messageBot,
  screen: screen,
  cache: cache,
});

const persistConfig: PersistConfig<RootState> = {
  key: "root",
  storage,
  blacklist: ["websocket", "notifications", "messageBot"],
  version: getVersionNumber(),
  debug: !isProd(),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const redux = configureStore({
  reducer: persistedReducer,
  devTools: false,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }),
});
const persistor = persistStore(redux);

export default redux;
export { persistor };

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof redux.dispatch;

export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
export type useGetState = () => RootState;
